import { redirect } from "react-router-dom";

import {
  DEFAULT_ROUTE,
  RISK_ROUTE_CHARTS_MAJOR,
  RISK_ROUTE_CHARTS_MAJOR_SEGMENT,
  RISK_ROUTE_CHARTS_MITIGATION,
  RISK_ROUTE_CHARTS_MITIGATION_SEGMENT,
  RISK_ROUTE_CHARTS_OVERALL,
  RISK_ROUTE_CHARTS_OVERALL_SEGMENT,
} from "constants/routing";
import { isNaN } from "lodash-es";
import type { FilteredWellNamesByHierarchy } from "contexts/filters/types";

const _riskPathOptions = [
  RISK_ROUTE_CHARTS_MITIGATION_SEGMENT,
  RISK_ROUTE_CHARTS_OVERALL_SEGMENT,
  RISK_ROUTE_CHARTS_MAJOR_SEGMENT,
] as const;
export type RiskPathOption = (typeof _riskPathOptions)[number];

export const getRiskChartsPath = (
  hierarchyId: number,
  wellName: string,
  option: RiskPathOption,
): string => {
  switch (option) {
    case RISK_ROUTE_CHARTS_MITIGATION_SEGMENT:
      return `${RISK_ROUTE_CHARTS_MITIGATION}/${hierarchyId}_${wellName}`;
    case RISK_ROUTE_CHARTS_OVERALL_SEGMENT:
      return `${RISK_ROUTE_CHARTS_OVERALL}/${hierarchyId}_${wellName}`;
    case RISK_ROUTE_CHARTS_MAJOR_SEGMENT:
      return `${RISK_ROUTE_CHARTS_MAJOR}/${hierarchyId}_${wellName}`;
  }
};

// parse well id name parameter
// returns [hierarchyId, wellName]
// hierarchyId is -1, well name is an empty string when invalid
export const parseWellIdNameParam = (wellIdName: string): [number, string] => {
  // split at first underscore always - don't use split to avoid double underscores
  const idx = wellIdName.indexOf("_");

  let hierarchyId: string;
  let wellName: string;

  // if no underscore, treat entire string as hierarchyId and wellName as not found
  if (idx === -1) {
    hierarchyId = wellIdName;
    wellName = "";
  } else {
    hierarchyId = wellIdName.slice(0, idx);
    wellName = wellIdName.slice(idx + 1);
  }

  // validate that hierarchyId is a number
  const coercedHierarchyId =
    hierarchyId.length === 0 || isNaN(Number(hierarchyId))
      ? -1
      : Number(hierarchyId);
  return [coercedHierarchyId, wellName];
};

// validate that well name and hierarchy id combination exists
// returns [hierarchyId, wellName]
// hierarchyId is -1, well name is an empty string when invalid
export const validateWellName = (
  hierarchyId: number,
  wellName: string,
  wellNamesByHierarchy: FilteredWellNamesByHierarchy,
): [number, string] => {
  // find the hierarchyId and wellName in the data
  const hierarchyIdNode = wellNamesByHierarchy.find(
    (hierarchy) => hierarchy.hierarchyId === hierarchyId,
  );
  const foundHierarchyId = hierarchyIdNode?.hierarchyId ?? -1;

  const foundWellName =
    hierarchyIdNode?.wellNames?.find((name) => name === wellName) ?? "";

  return [foundHierarchyId, foundWellName] as const;
};

export const makeRedirectTo = (defaultRoute = DEFAULT_ROUTE) => {
  return ({ request }: { request: Request }) => {
    const url = new URL(request.url);

    if (url.pathname === defaultRoute) {
      return null;
    }

    return redirect(`${defaultRoute}${url.search}`);
  };
};

export const redirectToDefault = makeRedirectTo();
